import { type PropsWithChildren, createContext, useContext, useState } from 'react';

import { Nullable, Optional } from '../types/utility';
import { ReportParameterDatePart, ReportParameterTypes } from '../logic/api';

export type UserParams = {
  "@u_startDate"?: Nullable<string>;
  "@u_endDate"?: Nullable<string>;
  "@u_driverIds"?: Nullable<string[]>;
  "@u_interval"?: Nullable<ReportParameterDatePart>;
  "@u_receptionistIds"?: Nullable<string[]>;
  "@u_chartInterval"?: Nullable<string>;
  "@u_daysToSell"?: Nullable<number>;
};

// All possible user parameters set to null by default
const DEFAULT_USER_PARAMS: UserParams = {
  "@u_startDate": null,
  "@u_endDate": null,
  "@u_driverIds": null,
  "@u_interval": null,
  "@u_receptionistIds": null,
  "@u_chartInterval": null,
  "@u_daysToSell": null
};

interface IUserParamsContext {
  userParams: UserParams;
  setUserParams: (params: UserParams | ((prev: UserParams) => UserParams)) => void;
  parameterTypes: { type: ReportParameterTypes; name: string }[];
  clearParams: (paramKeys: (keyof UserParams)[]) => void;
}

const UserParamsContext = createContext<Optional<IUserParamsContext>>(undefined);

export const useUserParams = (): IUserParamsContext => {
  const context = useContext(UserParamsContext);
  if (context === undefined) {
    throw new Error('useUserParams must be used within a UserParamsProvider');
  }

  return context;
};

export const UserParamsProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useState<UserParams>(DEFAULT_USER_PARAMS);

  const setUserParams = (params: UserParams | ((prev: UserParams) => UserParams)) => {
    setState((prev) => {
      const nextParams = typeof params === 'function' ? params(prev) : params;
      return { ...prev, ...nextParams };
    });
  };

  const clearParams = (paramKeys: (keyof UserParams)[]) => {
    setState((prev) => {
      const newState = { ...prev };

      paramKeys.forEach(key => {
        newState[key] = null;
      });

      return newState;
    });
  };

  const parameterTypes = Object.entries(state)
    .filter(([, value]) => value !== null)
    .map(([key]) => {
      let type: ReportParameterTypes = ReportParameterTypes.string;

      if (key === '@u_startDate' || key === '@u_endDate') {
        type = ReportParameterTypes.datetime;
      } else if (key === '@u_driverIds' || key === '@u_receptionistIds') {
        type = ReportParameterTypes.array;
      } else if (key === '@u_interval' || key === '@u_chartInterval') {
        type = ReportParameterTypes.datepart;
      } else if (key === '@u_daysToSell') {
        type = ReportParameterTypes.number;
      }

      return { name: key, type };
    });

  return (
    <UserParamsContext.Provider value={{
      userParams: state,
      setUserParams,
      parameterTypes,
      clearParams,
    }}>
      {children}
    </UserParamsContext.Provider>
  );
};
